import { useTranslation } from "react-i18next";
import { CCol, CRow } from "@coreui/react-pro";
import {
  dashboardOffers,
  type PartnerOfferDecision,
  usePartnerOffers,
  useRecordPartnerOfferDecision,
} from "../../../api/hooks/usePartnerOffers";
import { useToast } from "../../../context/useToast";
import { PartnerOfferCard } from "../../../components/common/PartnerOfferCard";

/**
 * The dashboard's partner offers.
 *
 * Every partner with something to say, not just the first. Which partners fill it, and how many,
 * stays the API's decision — it caps the list, so this never becomes a column of adverts.
 */
export function PartnerOfferSlot() {
  const { t } = useTranslation("partnerOffers");
  const { showSuccess } = useToast();
  const offers = usePartnerOffers();
  const record = useRecordPartnerOfferDecision();

  const due = dashboardOffers(offers.data);

  // Renders nothing at all while loading, on error, or when no partner is due. A partner offer is
  // never why someone opened the portal, so it should never be a spinner or an error box on the
  // dashboard — it just isn't there.
  if (due.length === 0) return null;

  const handleDecision = (partnerKey: string, decision: PartnerOfferDecision) => {
    record.mutate(
      { partnerKey, decision },
      {
        onSuccess: () => {
          if (decision === "AlreadyUsing") {
            // One click, no confirm dialog, but an undo — this is permanent, and the accuracy of
            // the "already using" figure is the thing worth protecting. In the toast rather than on
            // the Partner offers page: the moment a mis-click is still in someone's head is now.
            showSuccess(t("toast.alreadyUsing"), {
              label: t("toast.undo"),
              onClick: () => record.mutate({ partnerKey, decision: "Reopen" }),
            });
          } else if (decision === "Snooze") {
            showSuccess(t("toast.snoozed"));
          } else if (decision === "ConfirmSignedUp") {
            showSuccess(t("toast.signedUp"));
          }
        },
      }
    );
  };

  return (
    // Side by side from md up, so two offers read as a shelf rather than a stack of adverts; they
    // fall into a column only where the screen leaves no choice. The row carries the spacing the
    // card used to, so the dashboard is unchanged on the days no offer is due.
    <CRow xs={{ gutter: 4 }} className="mt-4 mb-4">
      {due.map((offer) => (
        // Full width when it is the only one left, rather than a half card against empty space.
        <CCol md={due.length > 1 ? 6 : 12} key={offer.partnerKey}>
          <PartnerOfferCard
            offer={offer}
            onDecision={(decision) => handleDecision(offer.partnerKey, decision)}
            busy={record.isPending}
            // h-100 so a pair lines up regardless of how long each one's copy runs; without it the
            // card with an offer line stands taller and the row reads as unbalanced.
            className="h-100"
          />
        </CCol>
      ))}
    </CRow>
  );
}

export default PartnerOfferSlot;
